// ============================================================================
// Page Metadata Composable
// ============================================================================

import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'

import { THUMBNAIL_PATH, DEFAULT_META, ROUTES_METADATA } from 'src/config/routesMetadata.config'

// Global state for dynamic data (shared across all instances)
const globalDynamicData = ref(null)

//Initialize page metadata system (called once in App.vue)
export function usePageMetadata() {
  const route = useRoute()
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''

  const headData = computed(() => {
    const routeName = route.name
    const routeConfig = ROUTES_METADATA[routeName]

    // skip routes with dynamic data that are handled by components
    if (routeConfig?.skipAutoMetaGeneration && !globalDynamicData.value) {
      return {}
    }

    let title = DEFAULT_META.title
    let description = DEFAULT_META.description

    // Check if route has dynamic metadata function
    if (routeConfig?.getDynamicMeta) {
      // Pass route object (reactive to param changes) and dynamic data
      const meta = routeConfig.getDynamicMeta(route, globalDynamicData.value)
      title = meta.title
      description = meta.description
    } else if (routeConfig?.meta) {
      // Use static metadata
      title = routeConfig.meta.title
      description = routeConfig.meta.description
    }

    const fullUrl = baseUrl ? `${baseUrl}${route.fullPath}` : ''
    const logoUrl = baseUrl ? `${baseUrl}${THUMBNAIL_PATH}` : THUMBNAIL_PATH

    return buildMetaTags(title, description, logoUrl, fullUrl)
  })

  // Apply metadata to head
  useHead(headData)
}

// exposed functions to set and clear dynamic data from components/other composables
export function setDynamicData(data) {
  globalDynamicData.value = globalDynamicData.value ? { ...globalDynamicData.value, ...data } : data
}

export function clearDynamicData() {
  globalDynamicData.value = null
}

function buildMetaTags(title, description, logoUrl, fullUrl) {
  return {
    title,
    meta: [
      {
        name: 'description',
        content: description,
      },
      // Open Graph tags
      {
        property: 'og:title',
        content: title,
      },
      {
        property: 'og:description',
        content: description,
      },
      {
        property: 'og:image',
        content: logoUrl,
      },
      {
        property: 'og:url',
        content: fullUrl,
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:site_name',
        content: DEFAULT_META.title,
      },
    ],
    link: [
      {
        rel: 'canonical',
        href: fullUrl,
      },
    ],
  }
}
